import React from 'react';
import { ArrowUp, LoaderCircle } from 'lucide-react';
import { AttachmentComposer } from '../chat/AttachmentComposer';
import { useI18n } from '../../hooks/useI18n';

export const ImageComposer = ({
  prompt,
  onPromptChange,
  onSubmit,
  onRetry,
  attachments = [],
  referencePreviewUrls = [],
  followUp = false,
  onAddFiles,
  onRemoveAttachment,
  sending = false,
  uploading = false,
  error = '',
  canRetry = false
}) => {
  const { t } = useI18n();
  const disabled = sending || uploading || !prompt.trim();
  const placeholder = followUp ? t('imageAskFollowUpPlaceholder') : t('imageAskPlaceholder');

  return (
    <form onSubmit={onSubmit} className="mx-auto w-full max-w-3xl">
      {attachments.length > 0 ? (
        <div className="mb-2 flex flex-wrap gap-2 px-1">
          {attachments.map((item) => (
            <div
              key={item.id}
              className="relative h-14 w-14 overflow-hidden rounded-xl border border-neutral-200 dark:border-neutral-800"
            >
              <img src={item.previewUrl || item.url} alt="" className="h-full w-full object-cover" />
              <button
                type="button"
                disabled={sending || uploading}
                onClick={() => onRemoveAttachment?.(item)}
                className="absolute end-1 top-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-[10px] text-white"
                aria-label={t('removeAttachment')}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      ) : referencePreviewUrls.length > 0 ? (
        <div className="mb-2 flex flex-wrap items-center gap-2 px-1">
          {referencePreviewUrls.map((url) => (
            <div
              key={url}
              className="relative h-14 w-14 overflow-hidden rounded-xl border border-neutral-200 dark:border-neutral-800"
            >
              <img src={url} alt="" className="h-full w-full object-cover" />
            </div>
          ))}
          <span className="text-xs text-neutral-500">{t('imageEditingPrevious')}</span>
        </div>
      ) : null}

      <div className="flex items-end gap-1 rounded-2xl border border-border bg-white px-2 py-2 shadow-sm dark:bg-panel-strong">
        <AttachmentComposer
          attachments={[]}
          attachmentCount={attachments.length}
          onAddFiles={onAddFiles}
          onRemove={onRemoveAttachment}
          disabled={sending}
          uploading={uploading}
          maxCount={5}
          variant="pill"
        />
        <textarea
          value={prompt}
          onChange={(event) => onPromptChange?.(event.target.value)}
          rows={1}
          placeholder={placeholder}
          aria-label={placeholder}
          disabled={sending}
          className="max-h-32 min-h-[40px] flex-1 resize-none bg-transparent py-2.5 text-base text-text outline-none placeholder:text-muted disabled:opacity-70 md:text-sm"
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              if (!disabled) {
                onSubmit?.(event);
              }
            }
          }}
        />
        <button
          type="submit"
          disabled={disabled}
          className="mb-0.5 inline-flex h-10 w-10 items-center justify-center rounded-full bg-neutral-950 text-white transition enabled:hover:bg-neutral-800 disabled:opacity-40 dark:bg-white dark:text-neutral-950"
          aria-label={t('imageGenerate')}
        >
          {sending ? <LoaderCircle size={16} className="animate-spin" aria-hidden /> : <ArrowUp size={16} aria-hidden />}
        </button>
      </div>
      {error ? (
        <div className="mt-2 flex flex-col items-center gap-2 text-center">
          <p className="text-sm text-red-500" role="alert">
            {error}
          </p>
          {canRetry ? (
            <button
              type="button"
              onClick={onRetry}
              className="text-sm font-medium text-neutral-700 underline-offset-2 hover:underline dark:text-neutral-200"
            >
              {t('imageRetry')}
            </button>
          ) : null}
        </div>
      ) : null}
    </form>
  );
};
